import { Button, Field, Input, Label } from "@cvpartner/design-system";
import { CheckCircle, XCircle } from "@cvpartner/design-system/icons/line";
import React from "react";
import { Form } from "react-aria-components";
import { type Static, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import { ReadonlyObject } from "@/shared/schema/schema.ts";
import { Header } from "@/shared/ui/header/Index.tsx";
import { httpChecked } from "@/shared/util/fetch.tsx";
import { I18n } from "@/shared/util/translations.ts";
import {
  requirementIcon,
  requirementText,
  requirementWrapper,
  requirementsWrapper,
} from "./PasswordChange.css.ts";
import { CardContent } from "./shared/LoginCard.tsx";
import { LoginButtonWrapper, Wrapper } from "./shared/Modern.tsx";

// Which input an error is about: the current password failing to authenticate,
// or the new one failing a rule.
const PasswordField = Type.Union([
  Type.Literal("old_password"),
  Type.Literal("new_password"),
]);

const PasswordResponse = Type.Union([
  ReadonlyObject({
    password_error: Type.String(),
    field: PasswordField,
    new_path: Type.Optional(Type.Never()),
  }),
  ReadonlyObject({
    password_error: Type.Optional(Type.Never()),
    field: Type.Optional(Type.Never()),
    new_path: Type.String(),
  }),
]);

type PasswordField = Static<typeof PasswordField>;

const PasswordResponseChecker = TypeCompiler.Compile(PasswordResponse);

interface BaseProps {
  submit_path: string;
  user_id: string | undefined;
  alert?: React.ReactNode;
}

export interface ChangeProps extends BaseProps {
  type: "change";
}

export interface ResetProps extends BaseProps {
  type: "reset";
  reset_password_code: string | undefined;
}

type Props = ChangeProps | ResetProps;

interface pcState {
  password_error: string;
  password_error_field: PasswordField;
  old_password: string;
  new_password: string;
  passwordIsRepetitive?: boolean;
  passwordIsShort?: boolean;
  is_loading: boolean;
}

type RequirementState = "neutral" | "valid" | "invalid";

function passwordRequirementToState(
  value: boolean | undefined,
): RequirementState {
  switch (value) {
    case true:
      return "invalid";
    case false:
      return "valid";
    case undefined:
      return "neutral";
  }
}

const PasswordRequirement: React.FC<{
  state: RequirementState;
  label: string;
}> = ({ state, label }) => (
  <div className={requirementWrapper}>
    {state === "neutral" || state === "valid" ? (
      <CheckCircle className={requirementIcon({ state })} />
    ) : (
      <XCircle className={requirementIcon({ state })} />
    )}
    <div className={requirementText}>{I18n.t(label)}</div>
  </div>
);

export class PasswordChange extends React.PureComponent<Props, pcState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      password_error: "",
      password_error_field: "new_password",
      old_password: "",
      new_password: "",
      passwordIsRepetitive: undefined,
      passwordIsShort: undefined,
      is_loading: false,
    };
  }

  // The error belongs to one input, so only editing that input clears it.
  password_error_after_editing(field: PasswordField, state: pcState) {
    return state.password_error_field === field ? "" : state.password_error;
  }

  handle_old_password_changed: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const old_password = e.target.value;

    this.setState((state) => ({
      old_password,
      password_error: this.password_error_after_editing("old_password", state),
    }));
  };

  handle_new_password_changed: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const new_password = e.target.value;

    this.run_passwordyness_checks(new_password);
    this.setState((state) => ({
      new_password,
      password_error: this.password_error_after_editing("new_password", state),
    }));
  };

  handle_submit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    this.setState({ is_loading: true });

    e.preventDefault();
    e.stopPropagation();

    const data = new FormData();
    if (this.props.type === "change") {
      data.append("old_password", this.state.old_password);
    }
    data.append("new_password", this.state.new_password);
    if (this.props.type === "reset") {
      data.append("reset_password_code", this.props.reset_password_code ?? "");
    }
    data.append("user_id", this.props.user_id || "");

    try {
      const json = await httpChecked.post(
        this.props.submit_path,
        PasswordResponseChecker,
        { body: data },
      );
      this.setState({
        password_error: json.password_error || "",
        password_error_field: json.field ?? "new_password",
        is_loading: false,
      });
      if (json.new_path) {
        window.location.assign(json.new_path);
      }
    } catch (error) {
      this.setState({ is_loading: false });

      throw error;
    }
  };

  run_passwordyness_checks = (candidate: string) => {
    if (candidate) {
      this.setState({
        passwordIsRepetitive: this.is_password_repetitive(candidate),
        passwordIsShort: this.is_password_short(candidate),
      });
    } else {
      this.setState({
        passwordIsRepetitive: undefined,
        passwordIsShort: undefined,
      });
    }
  };

  is_password_repetitive = (candidate: string) => {
    const l = Math.floor(candidate.length / 2);
    return candidate.includes(candidate.substring(0, l), l);
  };

  is_password_short = (candidate: string) => {
    return candidate.length < 8;
  };

  render() {
    return (
      <>
        <Header />
        <Wrapper
          heading={I18n.t(
            this.props.type === "reset"
              ? "resetting_password.header_title"
              : "changing_password.header_title",
          )}
          subheading={I18n.t("resetting_password.header_subtitle")}
          alert={this.props.alert}
          rebrandInfo={this.props.type === "reset"}
          breadcrumbs={
            this.props.type === "change"
              ? [{ label: I18n.t("changing_password.header_title") }]
              : undefined
          }
          hideLogo
        >
          <Form
            onSubmit={this.handle_submit}
            validationBehavior="aria"
            validationErrors={
              this.state.password_error
                ? {
                    [this.state.password_error_field]:
                      this.state.password_error,
                  }
                : undefined
            }
          >
            <CardContent>
              {this.props.type === "change" && (
                <Field name="old_password" type="password">
                  <Label>
                    {I18n.t("changing_password.label_current_password")}
                  </Label>
                  <Input
                    placeholder={I18n.t("signin.password_placeholder")}
                    onChange={this.handle_old_password_changed}
                    required={true}
                  />
                </Field>
              )}
              <Field name="new_password" type="password">
                <Label>{I18n.t("resetting_password.label_new_password")}</Label>
                <Input
                  placeholder={I18n.t("signin.password_placeholder")}
                  onChange={this.handle_new_password_changed}
                  required={true}
                />
              </Field>
              <div className={requirementsWrapper}>
                <PasswordRequirement
                  state={passwordRequirementToState(this.state.passwordIsShort)}
                  label="resetting_password.body_minimum_characters"
                />
                <PasswordRequirement
                  state={passwordRequirementToState(
                    this.state.passwordIsRepetitive,
                  )}
                  label="resetting_password.body_avoid_repetition"
                />
              </div>
              <LoginButtonWrapper center={this.state.is_loading}>
                {this.state.is_loading ? (
                  <Button
                    type="submit"
                    hierarchy="primary"
                    variant="iconOnly"
                    loading={true}
                  />
                ) : (
                  <Button
                    type="submit"
                    hierarchy="primary"
                    disabled={
                      this.state.passwordIsRepetitive ||
                      this.state.passwordIsShort ||
                      (this.props.type === "change"
                        ? !this.state.old_password || !this.state.new_password
                        : !this.state.new_password)
                    }
                  >
                    {I18n.t("resetting_password.bt_save_new_password")}
                  </Button>
                )}
              </LoginButtonWrapper>
            </CardContent>
          </Form>
        </Wrapper>
      </>
    );
  }
}
